/**
 * Orchestration transcript hygiene — keep infrastructure out of user-facing chat
 * and out of persisted conversation history sent back to the model.
 */

export const ORCHESTRATION_PROMPT_SEPARATOR = "\n\n---\n\n";

export function hasOrchestrationInfrastructureMarkers(text: string): boolean {
  return (
    text.includes("## Retrieved context (orchestration prefetch)") ||
    text.includes("## Context integrity (orchestration)") ||
    text.includes("### Session continuity (structured state)") ||
    text.includes("[ctx — internal, do not quote") ||
    text.includes("[orchestration]") ||
    text.includes("[length-continue — internal]") ||
    /\bhandoff_id:\s*[0-9a-f-]{36}\b/i.test(text)
  );
}

const ORCHESTRATION_MARKER_RE =
  /## Retrieved context \(orchestration prefetch\)|## Context integrity \(orchestration\)|### Session continuity \(structured state\)|\[ctx — internal, do not quote|\[orchestration\]|\[length-continue — internal\]|\bhandoff_id:\s*[0-9a-f-]{36}\b/i;

/** Index of the first orchestration infrastructure marker, or -1. */
export function findOrchestrationMarkerIndex(text: string): number {
  const match = ORCHESTRATION_MARKER_RE.exec(text);
  return match?.index ?? -1;
}

/**
 * Strip orchestration prefix from a message, keeping the real body.
 * Splits on the first separator after the orchestration marker so markdown
 * horizontal rules (`---`) inside the body are preserved.
 */
export function stripOrchestrationInfrastructure(text: string): string {
  if (!text.trim()) {
    return "";
  }

  const markerAt = findOrchestrationMarkerIndex(text);
  if (markerAt < 0) {
    return text;
  }

  const fromMarker = text.slice(markerAt);
  const separatorIndex = fromMarker.indexOf(ORCHESTRATION_PROMPT_SEPARATOR);
  if (separatorIndex >= 0) {
    return fromMarker.slice(separatorIndex + ORCHESTRATION_PROMPT_SEPARATOR.length).trim();
  }

  // Marker present without separator — entire message is infrastructure.
  return "";
}

function messageTextContent(message: { content?: unknown; text?: unknown }): string {
  if (typeof message.content === "string") {
    return message.content;
  }
  if (Array.isArray(message.content)) {
    return message.content
      .map((item) => {
        const x = item as { text?: unknown };
        return typeof x.text === "string" ? x.text : "";
      })
      .join("\n");
  }
  if (typeof message.text === "string") {
    return message.text;
  }
  return "";
}

function patchMessageText(
  message: Record<string, unknown>,
  stripped: string,
): Record<string, unknown> {
  if (typeof message.content === "string") {
    return { ...message, content: stripped };
  }
  if (Array.isArray(message.content)) {
    const next = message.content.map((item, index) => {
      if (index !== 0) {
        return item;
      }
      const entry = item as Record<string, unknown>;
      if (entry.type === "text" && typeof entry.text === "string") {
        return { ...entry, text: stripped };
      }
      return item;
    });
    return { ...message, content: next };
  }
  if (typeof message.text === "string") {
    return { ...message, text: stripped };
  }
  return message;
}

/**
 * True when a message is ENTIRELY an orchestration infrastructure block.
 *
 * The fleet broadcast path writes its `[orchestration] ## Orchestration
 * fleet-plan:…` status into the live session file while the parent's turn
 * is still mid-tool-call, so the row lands BETWEEN a `tool_use` and its
 * `tool_result`. Anthropic's tool protocol requires the `tool_result` to
 * follow its `tool_use` immediately, and MiniMax rejects the interleaved
 * shape outright:
 *
 *   HTTP 400 `invalid params, tool call result does not follow tool call
 *   (2013)` on MiniMax-M2.7, HTTP 500 on MiniMax-M3.
 *
 * The row carries no model-authored prose, so it can be dropped from the
 * model-facing transcript. It must START with the label — a reply that
 * merely mentions `[orchestration]` inside a sentence is real content.
 */
export function isOrchestrationInfrastructureText(text: string): boolean {
  if (!text.trimStart().startsWith("[orchestration]")) {
    return false;
  }
  return stripOrchestrationInfrastructure(text).trim() === "";
}

/** Remove orchestration prefix from session history entries before model reuse. */
export function stripOrchestrationFromHistoryMessages<
  T extends { role?: string; content?: unknown; text?: unknown },
>(messages: T[]): T[] {
  let changed = false;
  const next = messages.map((message) => {
    const role = typeof message.role === "string" ? message.role.toLowerCase() : "";
    if (role !== "user" && role !== "assistant") {
      return message;
    }
    const raw = messageTextContent(message);
    const stripped = stripOrchestrationInfrastructure(raw).trim();
    if (!stripped || stripped === raw.trim()) {
      return message;
    }
    changed = true;
    return patchMessageText(message as Record<string, unknown>, stripped) as T;
  });
  return changed ? next : messages;
}
